/*
 * <complete-the-kit> — "Complete the kit" bundle upsell.
 *
 * When the checkbox is UNCHECKED it does nothing: Dawn's own <product-form>
 * handler adds only the main variant (unchanged behaviour).
 *
 * When CHECKED it takes over the submit, reads the current cart, and adds only
 * the missing main/related variants in one /cart/add.js request (with a
 * `_bundle` line-item property). It then applies the real Shopify discount code
 * via /cart/update.js and refreshes/opens the cart drawer exactly like Dawn does.
 *
 * The 10% is computed server-side by Shopify from the discount code — this file
 * never fakes a price.
 */
if (!customElements.get('complete-the-kit')) {
  customElements.define(
    'complete-the-kit',
    class CompleteTheKit extends HTMLElement {
      connectedCallback() {
        this.checkbox = this.querySelector('.cro-kit__checkbox');
        this.form = document.getElementById(this.dataset.formId);
        if (!this.form) return;

        this.productForm = this.form.closest('product-form');
        this.interceptTarget = this.productForm || this.form;
        this.submitButton =
          document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`) ||
          this.form.querySelector('[type="submit"]');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.onSubmitCapture = this.onSubmitCapture.bind(this);
        // Capture phase on the ancestor <product-form> → runs before Dawn's
        // form-level submit handler, so we can pre-empt it when checked.
        this.interceptTarget.addEventListener('submit', this.onSubmitCapture, true);
      }

      disconnectedCallback() {
        if (this.interceptTarget) {
          this.interceptTarget.removeEventListener('submit', this.onSubmitCapture, true);
        }
      }

      get relatedItems() {
        try {
          return JSON.parse(this.dataset.kitItems || '[]');
        } catch (e) {
          return [];
        }
      }

      onSubmitCapture(evt) {
        // Unchecked → let Dawn's handler add only the main variant.
        if (!this.checkbox || !this.checkbox.checked) return;
        evt.preventDefault();
        evt.stopPropagation();
        this.addKit();
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;
        this.submitButton.classList.toggle('loading', isLoading);
        const spinner = this.submitButton.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !isLoading);
        if (isLoading) {
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else {
          this.submitButton.removeAttribute('aria-disabled');
        }
      }

      showError(message) {
        const wrapper = this.productForm && this.productForm.querySelector('.product-form__error-message-wrapper');
        if (!wrapper) return;
        wrapper.toggleAttribute('hidden', !message);
        const el = wrapper.querySelector('.product-form__error-message');
        if (el && message) el.textContent = message;
      }

      // Pick the related product's variant that matches the main product's
      // selected size when it's available; otherwise keep the first-available
      // variant (item.id) that Liquid already resolved.
      resolveVariantId(item) {
        const mainSize = (this.dataset.mainSize || '').trim().toLowerCase();
        if (!mainSize || !Array.isArray(item.variants)) return item.id;

        const match = item.variants.find(
          (variant) =>
            variant.available &&
            Array.isArray(variant.options) &&
            variant.options.some(
              (option) => /size/i.test(option.name) && String(option.value).trim().toLowerCase() === mainSize
            )
        );
        return match ? match.id : item.id;
      }

      buildItems() {
        const formData = new FormData(this.form);
        const mainId = formData.get('id');
        const mainQty = parseInt(formData.get('quantity'), 10) || 1;
        const bundleProps = { _bundle: 'complete-the-kit' };

        const items = [{ id: mainId, quantity: mainQty, properties: bundleProps }];
        this.relatedItems.forEach((item) => {
          if (!item || !item.id) return;
          items.push({ id: this.resolveVariantId(item), quantity: item.quantity || 1, properties: bundleProps });
        });
        return items;
      }

      getMissingItems(requestedItems, cartItems) {
        const existingVariantIds = new Set(
          (cartItems || [])
            .map((item) => item.variant_id ?? item.id)
            .filter(Boolean)
            .map(String)
        );
        const requestedVariantIds = new Set();

        return requestedItems.filter((item) => {
          if (!item || !item.id) return false;
          const variantId = String(item.id);
          if (existingVariantIds.has(variantId) || requestedVariantIds.has(variantId)) return false;
          requestedVariantIds.add(variantId);
          return true;
        });
      }

      async getCartState() {
        // window.Shopify.routes.root is never set in this theme (theme.liquid
        // exposes window.routes instead) — build the cart.js URL from the
        // routes object we actually have so this doesn't silently throw.
        const response = await fetch(`${routes.cart_url}.js`, {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });
        const cartState = await response.json();
        if (!response.ok || cartState.status) throw new Error(cartState.description || cartState.message);
        return cartState;
      }

      async addKit() {
        this.setLoading(true);
        this.showError(false);
        if (this.cart) this.cart.setActiveElement(document.activeElement);

        const sectionIds = this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : [];
        const jsonHeaders = {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/javascript',
        };

        try {
          // 1. Keep existing quantities unchanged and add only missing variants.
          const currentCart = await this.getCartState();
          const missingItems = this.getMissingItems(this.buildItems(), currentCart.items);

          if (missingItems.length > 0) {
            const addResponse = await fetch(routes.cart_add_url, {
              method: 'POST',
              headers: jsonHeaders,
              body: JSON.stringify({ items: missingItems }),
            });
            const addData = await addResponse.json();

            if (!addResponse.ok || addData.status) {
              this.showError(addData.description || addData.message);
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'complete-the-kit',
                message: addData.message,
                errors: addData.description,
              });
              return;
            }
          }

          // 2. Apply the real server-side discount code and fetch rendered
          //    cart sections in the same request. Reached only via this button.
          const code = (this.dataset.discountCode || '').trim();
          const updateBody = { sections: sectionIds, sections_url: window.location.pathname };
          if (code) updateBody.discount = code;

          const updateResponse = await fetch(routes.cart_update_url, {
            method: 'POST',
            headers: jsonHeaders,
            body: JSON.stringify(updateBody),
          });
          const cartState = await updateResponse.json();

          if (!updateResponse.ok || cartState.status) {
            this.showError(cartState.description || cartState.message);
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'complete-the-kit',
              message: cartState.message,
              errors: cartState.description,
            });
            return;
          }

          // 3. Refresh + open the drawer, mirroring Dawn's success path.
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'complete-the-kit', cartData: cartState });
          if (this.cart) {
            if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            this.cart.renderContents(cartState);
          } else {
            window.location = routes.cart_url;
          }
        } catch (e) {
          console.error(e);
          this.showError(window.cartStrings ? window.cartStrings.error : 'There was an error adding the kit.');
        } finally {
          this.setLoading(false);
        }
      }
    }
  );
}
